/**
 * CAPAManager v2 — Corrective and Preventive Actions.
 * Schema v2: sourceType, actions[] (max 3), task generation.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useCAPA } from '../../hooks/useCAPA';
import { useOrgUsers } from '../../hooks/useOrgUsers';
import { useCapaReferenceOptions } from '../../hooks/useCapaReferenceOptions';
import { parseAuditCapaSource } from '../../utils/capaAuditSource';
import './CAPAManager.css';

const SOURCE_TYPES = [
  { value: 'rca', labelKey: 'capa.sourceRca' },
  { value: 'audit', labelKey: 'capa.sourceAudit' },
  { value: 'fmea', labelKey: 'capa.sourceFmea' },
  { value: 'deviation', labelKey: 'capa.sourceDeviation' }
];

const ACTION_TYPES = [
  { value: 'corrective', labelKey: 'capa.actionTypeCorrective' },
  { value: 'preventive', labelKey: 'capa.actionTypePreventive' },
  { value: 'containment', labelKey: 'capa.actionTypeContainment' }
];

const CAPA_STATUS_BADGE = {
  open: 'badge-gray',
  in_progress: 'badge-amber',
  closed: 'badge-teal',
  planned: 'badge-gray',
  completed: 'badge-teal',
  verified: 'badge-blue'
};

const ACTION_STATUS_BADGE = {
  open: 'badge-gray',
  in_progress: 'badge-amber',
  verified: 'badge-teal',
  closed: 'badge-blue'
};

const emptyAction = () => ({
  actionId: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `action-${Date.now()}`,
  actionType: 'corrective',
  description: '',
  ownerId: '',
  ownerName: '',
  ownerGroupId: '',
  ownerGroupName: '',
  dueDate: '',
  protocolRef: ''
});

const emptyForm = (initialSourceType, initialRcaId, initialDeviationId, initialRiskId, initialSourceRef) => ({
  sourceType: initialSourceType || 'rca',
  rcaId: initialRcaId || '',
  deviationId: initialDeviationId || '',
  riskId: initialRiskId || '',
  auditFindingId: '',
  sourceRef: initialSourceRef || '',
  withoutRca: false,
  rcaReason: '',
  actions: [emptyAction()],
  effectivenessVerification: ''
});

const CAPAManager = ({
  organizationId,
  userId,
  userRole = 'administration',
  initialSourceType = null,
  initialRcaId = null,
  initialDeviationId = null,
  initialRiskId = null,
  initialSourceRef = null,
  singleEntryMode = false
}) => {
  // Default NS must be qms — keys like capa.* live only in qms (global defaultNS is common).
  const { t, i18n } = useTranslation('qms');
  const { items, loading, error, filters, setFilters, create, updateActionStatus, refetch } = useCAPA(organizationId);

  const { users: orgUsers, loading: orgUsersLoading } = useOrgUsers(organizationId);
  const {
    loading: refOptionsLoading,
    error: refOptionsError,
    rcas: rcaOptions,
    deviations: deviationOptions,
    auditFindings: auditFindingOptions,
    risks: riskOptions,
    groups: groupOptions
  } = useCapaReferenceOptions(organizationId, {
    initialRcaId,
    initialDeviationId,
    initialRiskId
  });

  const [showForm, setShowForm] = useState(singleEntryMode);
  const [expandedId, setExpandedId] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(
    emptyForm(initialSourceType, initialRcaId, initialDeviationId, initialRiskId, initialSourceRef)
  );

  const canCreate = ['management', 'ic_personnel'].includes(userRole);
  const canVerify = ['management', 'ic_personnel'].includes(userRole);

  const isPrePopulated = !!(
    initialSourceType &&
    (initialRcaId || initialDeviationId || initialRiskId || initialSourceRef)
  );

  const resetForm = () => {
    setForm(emptyForm(initialSourceType, initialRcaId, initialDeviationId, initialRiskId, initialSourceRef));
    setFormError(null);
    if (!singleEntryMode) setShowForm(false);
  };

  const addAction = () => {
    if (form.actions.length >= 3) return;
    setForm((prev) => ({ ...prev, actions: [...prev.actions, emptyAction()] }));
  };

  const removeAction = (actionId) => {
    setForm((prev) => ({ ...prev, actions: prev.actions.filter((a) => a.actionId !== actionId) }));
  };

  const updateAction = (actionId, field, value) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a) => (a.actionId === actionId ? { ...a, [field]: value } : a))
    }));
  };

  const auditSelectValue = useMemo(() => {
    if (!form.auditFindingId) return '';
    const f = auditFindingOptions.find((x) => x.findingId === form.auditFindingId);
    return f ? `${f.auditId}::${f.findingId}` : '';
  }, [form.auditFindingId, auditFindingOptions]);

  const handleAssignChange = (actionId, raw) => {
    if (!raw) {
      updateAction(actionId, 'ownerId', '');
      updateAction(actionId, 'ownerName', '');
      updateAction(actionId, 'ownerGroupId', '');
      updateAction(actionId, 'ownerGroupName', '');
      return;
    }
    if (raw.startsWith('u:')) {
      const id = raw.slice(2);
      const u = orgUsers.find((x) => x.userId === id);
      setForm((prev) => ({
        ...prev,
        actions: prev.actions.map((a) =>
          a.actionId === actionId
            ? {
                ...a,
                ownerId: id,
                ownerName: u?.fullName || '',
                ownerGroupId: '',
                ownerGroupName: ''
              }
            : a
        )
      }));
    } else if (raw.startsWith('g:')) {
      const id = raw.slice(2);
      const g = groupOptions.find((x) => x.id === id);
      setForm((prev) => ({
        ...prev,
        actions: prev.actions.map((a) =>
          a.actionId === actionId
            ? {
                ...a,
                ownerId: '',
                ownerName: '',
                ownerGroupId: id,
                ownerGroupName: g?.name || g?.label || id
              }
            : a
        )
      }));
    }
  };

  const formatRcaLabel = (r) => `${r.rca_code || r.id.slice(0, 8)}`;
  const formatDeviationLabel = (d) =>
    `${d.deviation_code || d.id.slice(0, 8)} — ${(d.description || '').trim().slice(0, 72)}`;
  const formatRiskLabel = (r) =>
    `${String(r.riskScenario || r.name || r.id).slice(0, 80)} (RPN ${r.rpn ?? '—'})`;

  const assignValueForAction = (action) => {
    if (action.ownerGroupId) return `g:${action.ownerGroupId}`;
    if (action.ownerId) return `u:${action.ownerId}`;
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate || !userId) return;
    setFormError(null);

    if (!form.sourceType) {
      setFormError(t('capa.errorSourceType'));
      return;
    }
    if (form.actions.length === 0) {
      setFormError(t('capa.errorNoActions'));
      return;
    }
    if (form.sourceType === 'deviation' && form.withoutRca && !form.rcaReason.trim()) {
      setFormError(t('capa.errorRcaReason'));
      return;
    }
    const invalidActions = form.actions.filter((a) => !a.description.trim());
    if (invalidActions.length > 0) {
      setFormError(t('capa.errorActionDescription'));
      return;
    }

    if (!isPrePopulated) {
      if (form.sourceType === 'rca' && !String(form.rcaId || '').trim()) {
        setFormError(t('capa.errorSourceIdRequired'));
        return;
      }
      if (form.sourceType === 'audit' && !String(form.auditFindingId || '').trim()) {
        setFormError(t('capa.errorSourceIdRequired'));
        return;
      }
      if (form.sourceType === 'fmea' && !String(form.riskId || '').trim()) {
        setFormError(t('capa.errorSourceIdRequired'));
        return;
      }
      if (form.sourceType === 'deviation' && !String(form.deviationId || '').trim()) {
        setFormError(t('capa.errorSourceIdRequired'));
        return;
      }
    }

    setSaving(true);
    try {
      const auditSource =
        form.sourceType === 'audit'
          ? parseAuditCapaSource(form.auditFindingId || null)
          : { auditId: null, auditFindingId: null };

      await create(
        {
          sourceType: form.sourceType,
          rcaId: form.sourceType === 'rca' ? form.rcaId || null : null,
          auditId: auditSource.auditId,
          auditFindingId: auditSource.auditFindingId,
          riskId: form.sourceType === 'fmea' ? form.riskId || null : null,
          deviationId: form.sourceType === 'deviation' ? form.deviationId || null : null,
          sourceRef: form.sourceRef || null,
          withoutRca: form.withoutRca,
          rcaReason: form.withoutRca ? form.rcaReason : null,
          actions: form.actions.map((a) => ({
            actionId: a.actionId,
            actionType: a.actionType,
            description: a.description,
            ownerId: a.ownerId || null,
            ownerName: a.ownerName || '',
            ownerGroupId: a.ownerGroupId || null,
            ownerGroupName: a.ownerGroupName || '',
            dueDate: a.dueDate || null,
            protocolRef: a.protocolRef || null
          })),
          effectivenessVerification: form.effectivenessVerification
        },
        userId
      );
      resetForm();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyAction = async (capaId, actionId) => {
    if (!canVerify || !userId) return;
    try {
      await updateActionStatus(capaId, actionId, 'verified', userId);
    } catch (err) {
      console.error('[CAPAManager] verify action failed:', err);
    }
  };

  const capaStatusLabel = (status) => {
    const map = {
      open: t('capa.statusOpen'),
      in_progress: t('capa.statusInProgress'),
      closed: t('capa.statusClosed'),
      planned: t('capa.statusPlanned'),
      completed: t('capa.statusCompleted'),
      verified: t('capa.statusVerified')
    };
    return map[status] || status;
  };

  const actionStatusLabel = (status) => {
    const map = {
      open: t('capa.actionStatusOpen'),
      in_progress: t('capa.actionStatusInProgress'),
      verified: t('capa.actionStatusVerified'),
      closed: t('capa.actionStatusClosed')
    };
    return map[status] || status;
  };

  const actionTypeLabel = (at) => {
    const map = {
      corrective: t('capa.actionTypeCorrective'),
      preventive: t('capa.actionTypePreventive'),
      containment: t('capa.actionTypeContainment')
    };
    return map[at] || at;
  };

  const sourceTypeLabel = (st) => {
    const map = {
      rca: t('capa.sourceRca'),
      audit: t('capa.sourceAudit'),
      fmea: t('capa.sourceFmea'),
      deviation: t('capa.sourceDeviation')
    };
    return map[st] || st;
  };

  const isOverdue = (item) => {
    if (item.status === 'closed' || item.status === 'verified') return false;
    const actions = item.actions || [];
    if (actions.length > 0) {
      return actions.some((a) => {
        if (a.status === 'verified' || a.status === 'closed') return false;
        if (!a.dueDate) return false;
        const due = a.dueDate?.toMillis?.() ?? new Date(a.dueDate).getTime();
        return due < Date.now();
      });
    }
    if (item.dueDate) {
      const due = item.dueDate?.toMillis?.() ?? new Date(item.dueDate).getTime();
      return due < Date.now();
    }
    return false;
  };

  if (!organizationId) {
    return (
      <div className="capa-manager">
        <p className="capa-empty">{t('capa.selectOrganization')}</p>
      </div>
    );
  }

  return (
    <div className="capa-manager">
      {!singleEntryMode && (
        <div className="capa-header">
          <h2>{t('capa.title')}</h2>
          {canCreate && (
            <button type="button" className="btn-primary" onClick={() => setShowForm(true)}>
              <Plus size={18} /> {t('capa.addCapa')}
            </button>
          )}
        </div>
      )}

      {!singleEntryMode && (
        <div className="capa-filters">
          <select
            value={filters.sourceType || ''}
            onChange={(e) => setFilters({ ...filters, sourceType: e.target.value || undefined })}
          >
            <option value="">{t('capa.filterSourceType')}</option>
            {SOURCE_TYPES.map((s) => (
              <option key={s.value} value={s.value}>
                {t(s.labelKey)}
              </option>
            ))}
          </select>
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
          >
            <option value="">{t('capa.filterStatus')}</option>
            <option value="open">{t('capa.statusOpen')}</option>
            <option value="in_progress">{t('capa.statusInProgress')}</option>
            <option value="closed">{t('capa.statusClosed')}</option>
            <option value="planned">{t('capa.statusPlanned')}</option>
            <option value="completed">{t('capa.statusCompleted')}</option>
          </select>
          <label>
            <input
              type="checkbox"
              checked={filters.overdueOnly || false}
              onChange={(e) => setFilters({ ...filters, overdueOnly: e.target.checked || undefined })}
            />
            {t('capa.overdueOnly')}
          </label>
        </div>
      )}

      {error && (
        <div className="capa-error">
          <AlertTriangle size={16} /> {error.message || String(error)}
        </div>
      )}
      {loading && <div className="capa-loading">{t('common:loading')}</div>}

      {showForm && (
        <form className="capa-form" onSubmit={handleSubmit}>
          {singleEntryMode && <h3>{t('capa.createTitle')}</h3>}

          {refOptionsLoading && (
            <p className="capa-ref-loading">{t('capa.refOptionsLoading')}</p>
          )}
          {refOptionsError && (
            <div className="capa-form-error capa-ref-error">
              <AlertTriangle size={14} /> {refOptionsError.message || String(refOptionsError)}
            </div>
          )}

          <div className="capa-form-section">
            <h4>{t('capa.sectionSource')}</h4>
            {!isPrePopulated && (
              <div className="form-row">
                <label>{t('capa.sourceType')} *</label>
                <div className="radio-group">
                  {SOURCE_TYPES.map((st) => (
                    <label key={st.value} className="radio-option">
                      <input
                        type="radio"
                        name="sourceType"
                        value={st.value}
                        checked={form.sourceType === st.value}
                        onChange={() => setForm((prev) => ({ ...prev, sourceType: st.value }))}
                        disabled={!canCreate}
                      />
                      {t(st.labelKey)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {isPrePopulated && (
              <div className="capa-source-info">
                <span className="source-badge">{sourceTypeLabel(form.sourceType)}</span>
                {form.sourceRef && <span className="source-ref">{form.sourceRef}</span>}
              </div>
            )}

            {form.sourceType === 'deviation' && isPrePopulated && (
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.withoutRca}
                    onChange={(e) => setForm((prev) => ({ ...prev, withoutRca: e.target.checked }))}
                    disabled={!canCreate}
                  />
                  {t('capa.withoutRca')}
                </label>
                {form.withoutRca && (
                  <textarea
                    value={form.rcaReason}
                    onChange={(e) => setForm((prev) => ({ ...prev, rcaReason: e.target.value }))}
                    placeholder={t('capa.rcaReasonPlaceholder')}
                    rows={2}
                    required
                    disabled={!canCreate}
                    className="form-input"
                  />
                )}
              </div>
            )}

            {form.sourceType === 'rca' && !isPrePopulated && (
              <div className="form-row">
                <label>{t('capa.rcaId')} *</label>
                <select
                  value={form.rcaId}
                  onChange={(e) => setForm((prev) => ({ ...prev, rcaId: e.target.value }))}
                  disabled={!canCreate || refOptionsLoading}
                  className="form-input capa-select"
                >
                  <option value="">{t('capa.selectRcaPlaceholder')}</option>
                  {rcaOptions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {formatRcaLabel(r)}
                    </option>
                  ))}
                </select>
                {rcaOptions.length === 0 && !refOptionsLoading && (
                  <span className="capa-ref-empty">{t('capa.noEligibleRcas')}</span>
                )}
              </div>
            )}
            {form.sourceType === 'audit' && !isPrePopulated && (
              <div className="form-row">
                <label>{t('capa.auditFindingLabel')} *</label>
                <select
                  value={auditSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setForm((prev) => ({ ...prev, auditFindingId: '' }));
                      return;
                    }
                    const sep = v.indexOf('::');
                    const fid = sep >= 0 ? v.slice(sep + 2) : v;
                    setForm((prev) => ({ ...prev, auditFindingId: fid }));
                  }}
                  disabled={!canCreate || refOptionsLoading}
                  className="form-input capa-select"
                >
                  <option value="">{t('capa.selectAuditFindingPlaceholder')}</option>
                  {auditFindingOptions.map((f) => (
                    <option key={`${f.auditId}::${f.findingId}`} value={`${f.auditId}::${f.findingId}`}>
                      {f.label}
                    </option>
                  ))}
                </select>
                {auditFindingOptions.length === 0 && !refOptionsLoading && (
                  <span className="capa-ref-empty">{t('capa.noEligibleAuditFindings')}</span>
                )}
              </div>
            )}
            {form.sourceType === 'fmea' && !isPrePopulated && (
              <div className="form-row">
                <label>{t('capa.riskId')} *</label>
                <select
                  value={form.riskId}
                  onChange={(e) => setForm((prev) => ({ ...prev, riskId: e.target.value }))}
                  disabled={!canCreate || refOptionsLoading}
                  className="form-input capa-select"
                >
                  <option value="">{t('capa.selectRiskPlaceholder')}</option>
                  {riskOptions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {formatRiskLabel(r)}
                    </option>
                  ))}
                </select>
                {riskOptions.length === 0 && !refOptionsLoading && (
                  <span className="capa-ref-empty">{t('capa.noEligibleRisks')}</span>
                )}
              </div>
            )}
            {form.sourceType === 'deviation' && !isPrePopulated && (
              <>
                <div className="form-row">
                  <label>{t('capa.deviationId')} *</label>
                  <select
                    value={form.deviationId}
                    onChange={(e) => setForm((prev) => ({ ...prev, deviationId: e.target.value }))}
                    disabled={!canCreate || refOptionsLoading}
                    className="form-input capa-select"
                  >
                    <option value="">{t('capa.selectDeviationPlaceholder')}</option>
                    {deviationOptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {formatDeviationLabel(d)}
                      </option>
                    ))}
                  </select>
                  {deviationOptions.length === 0 && !refOptionsLoading && (
                    <span className="capa-ref-empty">{t('capa.noEligibleDeviations')}</span>
                  )}
                </div>
                <div className="form-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.withoutRca}
                      onChange={(e) => setForm((prev) => ({ ...prev, withoutRca: e.target.checked }))}
                      disabled={!canCreate}
                    />
                    {t('capa.withoutRca')}
                  </label>
                  {form.withoutRca && (
                    <textarea
                      value={form.rcaReason}
                      onChange={(e) => setForm((prev) => ({ ...prev, rcaReason: e.target.value }))}
                      placeholder={t('capa.rcaReasonPlaceholder')}
                      rows={2}
                      required
                      disabled={!canCreate}
                      className="form-input"
                    />
                  )}
                </div>
              </>
            )}

            <div className="form-row">
              <label>{t('capa.sourceRef')}</label>
              <input
                type="text"
                value={form.sourceRef}
                onChange={(e) => setForm((prev) => ({ ...prev, sourceRef: e.target.value }))}
                disabled={!canCreate}
                className="form-input"
              />
            </div>
          </div>

          <div className="capa-form-section">
            <div className="section-header-row">
              <h4>{t('capa.sectionActions')}</h4>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={addAction}
                disabled={!canCreate || form.actions.length >= 3}
                title={form.actions.length >= 3 ? t('capa.maxActionsReached') : ''}
              >
                <Plus size={14} /> {t('capa.addActionBtn')}
              </button>
            </div>
            {form.actions.map((action, idx) => (
              <div key={action.actionId} className="capa-action-row">
                <div className="action-row-header">
                  <span className="capa-action-number">
                    {t('capa.action')} #{idx + 1}
                  </span>
                  {form.actions.length > 1 && canCreate && (
                    <button
                      type="button"
                      className="btn-icon danger"
                      onClick={() => removeAction(action.actionId)}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="action-type-row">
                  {ACTION_TYPES.map((at) => (
                    <label key={at.value} className="radio-option">
                      <input
                        type="radio"
                        name={`actionType-${action.actionId}`}
                        value={at.value}
                        checked={action.actionType === at.value}
                        onChange={() => updateAction(action.actionId, 'actionType', at.value)}
                        disabled={!canCreate}
                      />
                      {t(at.labelKey)}
                    </label>
                  ))}
                </div>
                <div className="form-row">
                  <label>{t('capa.actionDescription')} *</label>
                  <textarea
                    value={action.description}
                    onChange={(e) => updateAction(action.actionId, 'description', e.target.value)}
                    rows={2}
                    required
                    disabled={!canCreate}
                    className="form-input"
                  />
                </div>
                <div className="two-cols">
                  <div className="form-row">
                    <label>{t('capa.assignResponsible')}</label>
                    <select
                      value={assignValueForAction(action)}
                      onChange={(e) => handleAssignChange(action.actionId, e.target.value)}
                      disabled={!canCreate || orgUsersLoading}
                      className="form-input capa-select"
                    >
                      <option value="">{t('capa.assignPlaceholder')}</option>
                      <optgroup label={t('capa.optgroupUsers')}>
                        {orgUsers.map((u) => (
                          <option key={u.userId} value={`u:${u.userId}`}>
                            {u.fullName}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('capa.optgroupGroups')}>
                        {groupOptions.map((g) => (
                          <option key={g.id} value={`g:${g.id}`}>
                            {g.name || g.label || g.id}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    {orgUsers.length === 0 && groupOptions.length === 0 && !orgUsersLoading && (
                      <span className="capa-ref-empty">{t('capa.noUsersOrGroups')}</span>
                    )}
                  </div>
                  <div className="form-row capa-date-field">
                    <label>{t('capa.dueDate')}</label>
                    <input
                      type="date"
                      value={action.dueDate}
                      onChange={(e) => updateAction(action.actionId, 'dueDate', e.target.value)}
                      disabled={!canCreate}
                      className="form-input"
                      lang={i18n.language?.startsWith('ro') ? 'ro' : 'en'}
                    />
                    <span className="capa-date-hint">{t('capa.dateInputHint')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {formError && (
            <div className="capa-form-error">
              <AlertTriangle size={14} /> {formError}
            </div>
          )}

          <div className="capa-form-footer">
            <button type="button" onClick={resetForm} disabled={saving}>
              {t('common:cancel')}
            </button>
            {canCreate && (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('capa.saving') : t('capa.saveAndGenerate')}
              </button>
            )}
          </div>
        </form>
      )}

      {!singleEntryMode && (
        <div className="capa-list">
          {items.length === 0 && !loading && <p className="capa-empty">{t('capa.noActions')}</p>}
          {items.map((item) => (
            <div
              key={item.id}
              className={`capa-card ${isOverdue(item) ? 'overdue' : ''} status-${item.status}`}
            >
              <div
                className="capa-card-header"
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setExpandedId(expandedId === item.id ? null : item.id)}
              >
                <div className="capa-card-title">
                  <span className="capa-code">{item.capa_code || item.id.slice(0, 8)}</span>
                  <span className={`capa-status-badge ${CAPA_STATUS_BADGE[item.status] || 'badge-gray'}`}>
                    {capaStatusLabel(item.status)}
                  </span>
                  {isOverdue(item) && (
                    <span className="capa-overdue-badge">
                      <AlertTriangle size={12} /> {t('capa.overdue')}
                    </span>
                  )}
                </div>
                <div className="capa-card-meta">
                  {item.sourceRef && <span>{item.sourceRef}</span>}
                  {item.sourceType && (
                    <span className="source-type-chip">{sourceTypeLabel(item.sourceType)}</span>
                  )}
                  {expandedId === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {expandedId === item.id && (
                <div className="capa-card-detail">
                  {(item.actions || []).map((action) => (
                    <div key={action.actionId} className={`capa-action-detail status-${action.status}`}>
                      <div className="action-detail-header">
                        <span className="action-type-label">{actionTypeLabel(action.actionType)}</span>
                        <span className={`action-status-badge ${ACTION_STATUS_BADGE[action.status] || 'badge-gray'}`}>
                          {actionStatusLabel(action.status)}
                        </span>
                      </div>
                      <p className="action-description">{action.description}</p>
                      <div className="action-meta">
                        {action.ownerName && (
                          <span>
                            {t('capa.owner')}: {action.ownerName}
                          </span>
                        )}
                        {action.dueDate && (
                          <span>
                            {t('capa.due')}:{' '}
                            {action.dueDate.toDate?.().toLocaleDateString('ro-RO') ??
                              new Date(action.dueDate).toLocaleDateString('ro-RO')}
                          </span>
                        )}
                        {action.taskInstanceId && (
                          <span className="task-ref">
                            Task: {action.taskInstanceId.slice(0, 8)}…
                          </span>
                        )}
                      </div>
                      {canVerify && action.status !== 'verified' && action.status !== 'closed' && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => handleVerifyAction(item.id, action.actionId)}
                        >
                          <CheckCircle size={14} /> {t('capa.markVerified')}
                        </button>
                      )}
                      {action.status === 'verified' && (
                        <span className="verified-label">
                          <CheckCircle size={14} /> {t('capa.actionStatusVerified')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CAPAManager;
